import { useCallback, useEffect, useState } from 'react';

import AgoraRTC from 'agora-rtc-sdk-ng';

import { useAppContext } from '@/app/AppContext';
import Button from "@ergeon/core-components/exports/Button.ts";

import { ArrowPathRoundedSquareIcon } from "@ergeon/icons/hero/outline";
import { EVENTS, gtag } from '@/lib/gtag';

/**
 * Flipping to the rear camera is the whole point of the walkthrough, and the
 * account manager talks the customer into it by colour — "tap the blue button"
 * is how 37% of recorded calls name this control, against three calls in forty
 * thousand that call it the flip button.
 */
export default function ButtonSwitchCamera() {
  const appState = useAppContext();

  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  // Which way the camera points is tracked from this button, not read back from
  // getSettings(): mobile Safari reports 'environment' for a front camera, and
  // believing it un-mirrors the customer's own face — they move right and their
  // reflection goes left.
  const [facingMode, setFacingMode] = useState<VideoFacingModeEnum>('user');

  const toggleFacingMode = useCallback(() => {
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    appState.camera.changeDevice({ facingMode: newFacingMode });
    appState.setIsMainVideoMirrored(newFacingMode === 'user');

    gtag(EVENTS.CHANGE_DEVICE, { facingMode: newFacingMode });
  }, [appState, facingMode]);

  useEffect(function initCameras() {
    if (appState.camera.isPermissionGranted) {
      AgoraRTC.getCameras().then(setCameras, () => console.debug('Unable to access cameras'));
    }
  }, [appState.camera]);

  // Nothing to flip between on a laptop with one webcam, and a labelled button
  // that cannot do what it says is worse than no button. A device does not
  // report which way a camera points without opening it, so a second camera is
  // the available stand-in for "front and back" — true of the phones this is
  // for, and the same count the button was greyed out by before.
  if (cameras.length <= 1) {
    return null;
  }

  return (
    <Button
      // Blue on purpose: the AM script tells the customer to "tap the blue
      // button" to flip the camera, so this one has to stay findable by colour.
      flavor="primary"
      onClick={toggleFacingMode}
      shape="circle"
      size="large"
      title="Flip camera">
      <ArrowPathRoundedSquareIcon className="size-6 shrink-0" />
      Flip camera
    </Button>
  );
}
